// src/App.js

import React, { createContext, useState, useContext, useEffect } from 'react';
import './App.css';
import { CartProvider } from './context/CartContext';
import BucksHeader from './components/BucksHeader';
import BucksLanding from './components/BucksLanding';
import BucksShop from './components/BucksShop';
import BucksAbout from './components/BucksAbout';
import BucksContact from './components/BucksContact';
import BucksBox from './components/BucksBox';
import BucksRecipes from './components/BucksRecipes';
import BucksBasket from './components/BucksBasket';
import BucksCheckout from './components/BucksCheckout';
import BucksAdminLogin from './components/BucksAdminLogin';
import BucksAdminDashboard from './components/BucksAdminDashboard';
import BucksWorkerDashboard from './components/BucksWorkerDashboard';
import IdleTimerProvider from './components/IdleTimerProvider';
import BucksFooter from './components/BucksFooter';
import BucksPrivacyPolicy from './components/BucksPrivacyPolicy';
import BucksTermsOfService from './components/BucksTermsOfService';

// Create a Search Context to share search data across components if needed
export const SearchContext = createContext();
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// Create Auth Context for admin authentication
export const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Session timeout configuration (in milliseconds)
const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const WARNING_TIME    =  1 * 60 * 1000; // 1 minute warning before logout

function App() {
  // Simple routing based on pathname
  const pathname     = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // Dynamically set document.title based on current route
  useEffect(() => {
    const titles = {
      '/':                  'Home | Bucks Farmstand',
      '/shop':              'Shop | Bucks Farmstand',
      '/about':             'About | Bucks Farmstand',
      '/contact':           'Contact | Bucks Farmstand',
      '/bucks-box':         'Bucks Box | Bucks Farmstand',
      '/recipes':           'Recipes | Bucks Farmstand',
      '/basket':            'Your Basket | Bucks Farmstand',
      '/checkout':          'Checkout | Bucks Farmstand',
      '/privacy':           'Privacy Policy | Bucks Farmstand',
      '/terms':             'Terms of Service | Bucks Farmstand',
      '/admin/login':       'Admin Login | Bucks Farmstand',
      '/admin':             'Admin Dashboard | Bucks Farmstand',
      '/admin/dashboard':   'Admin Dashboard | Bucks Farmstand',
      '/worker/dashboard':  'Worker Dashboard | Bucks Farmstand'
    };

    let title = titles[pathname] || 'Bucks Farmstand';

    // Order confirmation page with order number
    if (pathname === '/order-confirmation') {
      const orderNum = searchParams.get('order');
      title = orderNum
        ? `Order Confirmation #${orderNum} | Bucks Farmstand`
        : 'Order Confirmation | Bucks Farmstand';
    }

    // Basket with checkout query param
    if (pathname === '/basket' && searchParams.get('checkout') === 'true') {
      title = 'Checkout | Bucks Farmstand';
    }

    document.title = title;
  }, [pathname, searchParams]);

  // Search context state
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching]     = useState(false);

  // Auth context state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser]             = useState(null);
  const [authLoading, setAuthLoading]         = useState(true);

  // Track the last activity timestamp
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Helper function to determine user dashboard based on role
  const getUserDashboardRoute = (user) => {
    if (!user || !user.role) return '/admin/login';
    
    switch (user.role.toLowerCase()) {
      case 'worker':
        return '/worker/dashboard';
      case 'owner':
      case 'manager':
      case 'viewer':
      default:
        return '/admin/dashboard';
    }
  };

  // Helper function to get the appropriate dashboard component
  const getDashboardComponent = (user) => {
    if (!user || !user.role) return null;
    
    switch (user.role.toLowerCase()) {
      case 'worker':
        return (
          <IdleTimerProvider timeout={SESSION_TIMEOUT} warningTime={WARNING_TIME}>
            <BucksWorkerDashboard />
          </IdleTimerProvider>
        );
      case 'owner':
      case 'manager':
      case 'viewer':
      default:
        return (
          <IdleTimerProvider timeout={SESSION_TIMEOUT} warningTime={WARNING_TIME}>
            <BucksAdminDashboard />
          </IdleTimerProvider>
        );
    }
  };

  // Check for existing authentication on mount and periodically
  useEffect(() => {
    const checkAuth = () => {
      const token            = sessionStorage.getItem('adminToken');
      const lastActivityTime = parseInt(sessionStorage.getItem('lastActivity') || '0', 10);
      const expiresAt        = sessionStorage.getItem('expiresAt');

      let isExpired = false;

      // Inactivity expiration
      if (lastActivityTime && Date.now() - lastActivityTime > SESSION_TIMEOUT) {
        isExpired = true;
      }
      // Token expiration
      if (expiresAt && new Date(expiresAt) < new Date()) {
        isExpired = true;
      }

      if (token && !isExpired) {
        setIsAuthenticated(true);
        try {
          const userData = JSON.parse(sessionStorage.getItem('adminUser') || '{}');
          setAdminUser(userData);
          
          // Auto-redirect to appropriate dashboard if on login page
          if (pathname === '/admin/login') {
            const dashboardRoute = getUserDashboardRoute(userData);
            window.location.href = dashboardRoute;
            return;
          }
          
          // Update last activity timestamp
          sessionStorage.setItem('lastActivity', Date.now().toString());
          setLastActivity(Date.now());
        } catch (error) {
          console.error('Error parsing admin user data:', error);
          logout();
        }
      } else {
        // Clear all auth data on expiration or missing token
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminUser');
        sessionStorage.removeItem('lastActivity');
        sessionStorage.removeItem('expiresAt');
        setIsAuthenticated(false);
      }

      setAuthLoading(false);
    };

    // Initial check
    checkAuth();
    // Periodic check every minute
    const authCheckInterval = setInterval(checkAuth, 60 * 1000);
    return () => clearInterval(authCheckInterval);
  }, [pathname]);

  // Login function for auth context
  const login = (userData, token, expiresAt) => {
    setIsAuthenticated(true);
    setAdminUser(userData);

    sessionStorage.setItem('adminToken', token || 'demo-token');
    sessionStorage.setItem('adminUser', JSON.stringify(userData));
    sessionStorage.setItem('lastActivity', Date.now().toString());
    if (expiresAt) {
      sessionStorage.setItem('expiresAt', expiresAt);
    }
    setLastActivity(Date.now());
    
    // Redirect to appropriate dashboard based on role
    const dashboardRoute = getUserDashboardRoute(userData);
    window.location.href = dashboardRoute;
  };

  // Logout function for auth context
  const logout = () => {
    setIsAuthenticated(false);
    setAdminUser(null);

    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    sessionStorage.removeItem('lastActivity');
    sessionStorage.removeItem('expiresAt');

    window.location.href = '/admin/login';
  };

  // Auth context value, now including expiresAt
  const authContextValue = {
    isAuthenticated,
    adminUser,
    login,
    logout,
    expiresAt:   sessionStorage.getItem('expiresAt') || null,
    lastActivity,
    setLastActivity
  };

  // Render component based on route, wrapping admin routes with IdleTimerProvider
  const renderPage = () => {
    if (authLoading && (pathname.startsWith('/admin') || pathname.startsWith('/worker'))) {
      return <div className="admin-loading">Loading...</div>;
    }

    switch (pathname) {
      case '/shop':
        return <BucksShop />;
      case '/about':
        return <BucksAbout />;
      case '/contact':
        return <BucksContact />;
      case '/bucks-box':
        return <BucksBox />;
      case '/recipes':
        return <BucksRecipes />;
      case '/privacy':
        return <BucksPrivacyPolicy />;
      case '/terms':
        return <BucksTermsOfService />;
      case '/basket':
        if (searchParams.get('checkout') === 'true') {
          return <BucksCheckout />;
        }
        return <BucksBasket />;
      case '/checkout':
        return <BucksCheckout />;
      case '/order-confirmation':
        return (
          <BucksCheckout
            initialOrderComplete={true}
            orderNumber={searchParams.get('order')}
          />
        );
      case '/admin/login':
        return isAuthenticated ? (
          // If already authenticated, redirect to appropriate dashboard
          getDashboardComponent(adminUser)
        ) : (
          <BucksAdminLogin />
        );
      case '/admin':
      case '/admin/dashboard':
        if (!isAuthenticated) {
          return <BucksAdminLogin />;
        }
        // Check if user should be on worker dashboard instead
        if (adminUser?.role?.toLowerCase() === 'worker') {
          window.location.href = '/worker/dashboard';
          return <div className="admin-loading">Redirecting...</div>;
        }
        return (
          <IdleTimerProvider timeout={SESSION_TIMEOUT} warningTime={WARNING_TIME}>
            <BucksAdminDashboard />
          </IdleTimerProvider>
        );
      case '/worker/dashboard':
        if (!isAuthenticated) {
          return <BucksAdminLogin />;
        }
        // Check if user should be on admin dashboard instead
        if (adminUser?.role?.toLowerCase() !== 'worker') {
          window.location.href = '/admin/dashboard';
          return <div className="admin-loading">Redirecting...</div>;
        }
        return (
          <IdleTimerProvider timeout={SESSION_TIMEOUT} warningTime={WARNING_TIME}>
            <BucksWorkerDashboard />
          </IdleTimerProvider>
        );
      default:
        return <BucksLanding />;
    }
  };

  // Search context value
  const searchContextValue = {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching
  };

  // Determine if we're on an admin/worker route
  const isAdminRoute = pathname.startsWith('/admin') || pathname.startsWith('/worker');

  return (
    <div className="App">
      <CartProvider>
        <SearchContext.Provider value={searchContextValue}>
          <AuthContext.Provider value={authContextValue}>
            {!isAdminRoute && <BucksHeader />}
            {renderPage()}
            {!isAdminRoute && <BucksFooter />}
          </AuthContext.Provider>
        </SearchContext.Provider>
      </CartProvider>
    </div>
  );
}

export default App;